<template>
    <div>
        <div class="grid gap-2">
            <div class="invoice__details__item py-2">
                <div class="col-12 mb--10 flex align-items-center justify-content-between">
                    <FormLabel title="Client information" html-for="info" label-type="title" />
                    <BaseButton 
                        variant="ghost" 
                        label="Edit" 
                        medium
                        prefix-icon
                        icon-name="edit"
                        @submit="openDetailsModal('clientDetails')"           
                    />
                </div>
    
                <div class="grid client-item">
                    <div>
                        <div>
                            <FormLabel title="Email" html-for="email" label-type="menu" />
                            <p class="invoice__detail text--sm">
                                {{ currentClient.email || 'N/A' }}
                            </p>
                        </div>
                    </div>
                        
                    <div>
                        <div>
                            <FormLabel title="Industry" html-for="industry" label-type="menu" />
                            <p class="text--capitalize invoice__detail text--sm">
                                {{ currentClient.industry || 'N/A' }}
                            </p>
                        </div>
                    </div>
    
                    <div>
                        <div>
                            <FormLabel title="Phone number" html-for="phoneNumber" label-type="menu" />
                            <p class="text--capitalize invoice__detail text--sm">
                                {{ currentClient.phoneNumber || '-' }}
                            </p>
                        </div>
                    </div>
    
                    <div>
                        <div>
                            <FormLabel title="Country" html-for="country" label-type="menu" />
                            <p class="text--capitalize invoice__detail text--sm">
                                {{ currentClient.country || "-" }}
                            </p>
                        </div>
                    </div>
    
                    <div>
                        <div>
                            <FormLabel title="City" html-for="city" label-type="menu" />
                            <p class="text--capitalize invoice__detail text--sm">
                                {{ currentClient.city || "-" }}
                            </p>
                        </div>
                    </div>
    
                    <div>
                        <div>
                            <FormLabel title="State" html-for="State" label-type="menu" />
                            <p class="text--capitalize invoice__detail text--sm">
                                {{ currentClient.state || "-" }}
                            </p>
                        </div>
                    </div>
    
                    <div>
                        <div>
                            <FormLabel title="Zip code" html-for="zipCode" label-type="menu" />
                            <p class="text--capitalize invoice__detail text--sm">
                                {{ currentClient.zipCode || "-" }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
    
            <div class="invoice__details__item py-2">
                <div class="col-12 mb--5 flex align-items-center justify-content-between">
                    <FormLabel title="Corresponding Emails" html-for="ccEmails" label-type="title" />
                    <BaseButton 
                        variant="ghost" 
                        label="Manage emails" 
                        medium
                        prefix-icon
                        icon-name="mail-send"
                        @submit="openDetailsModal('billingEmails')"            
                    />
                </div>
    
                <div class="email__wrap">
                    <div v-if="!currentClient.ccEmails.length">
                        <span class="text--sm text-gray-600">
                            N/A
                        </span>
                    </div>
    
                    <div v-else class="">
                        <p v-for="email in currentClient.ccEmails" :key="email" class="sub__tag text--sm text-gray-600 mb--5">
                            {{ email || '-' }}
                        </p>
                    </div>
                </div>
            </div>   
                
            <div class="invoice__details__item py-2">
                <div class="col-12 flex align-items-center justify-content-between">
                    <FormLabel title="Links" html-for="ccEmails" label-type="title" />
                    <BaseButton 
                        variant="ghost" 
                        label="+ Add new link" 
                        medium
                        @submit="modalAction = 'showAddLinkModal'"            
                    />
                </div>
    
                <div class="col-12 mb--10">
                    <div>
                        <template v-if="loadingState === 'fetch_link_loading'">
                            <PageSpinner size="sm" />
                        </template>
    
                        <template v-else-if="loadingState === 'fetch_link_error'">
                            <EmptyPage state="error" @action="handleFetchProjectLinks" />
                        </template>
    
                        <template v-else>
                            <div v-if="currentClient.links && currentClient.links.length" class="mt--15 grid gap-3">
                                <div 
                                    v-for="link in currentClient.links" 
                                    :key="link._id" 
                                    class="flex align-items-center justify-content-between w-100 flex-wrap"
                                >
                                    <div class="flex align-items-center">
                                        <a 
                                            class="project__item__title text--normal mt--0 text--sm text--underline" 
                                            :href="link.url"
                                            target="_blank"
                                        >
                                            {{ link.name }}
                                        </a>
                                    </div>
                                        
                                    <div class="flex align-items-center gap-3">
                                        <BaseButton 
                                            variant="outline"
                                            variant-style="secondary"
                                            label="Edit"
                                            @submit="openEditLinkModal(link)"
                                        />
                                        <BaseButton 
                                            variant="outline"
                                            variant-style="warning"
                                            label="Delete"
                                            :loading="loadingState === 'delete_link_loading' && linkIdToDelete === link._id"
                                            @submit="deleteLink(link._id)"
                                        />
                                    </div>
                                </div>
                            </div>            
                        </template>    
                    </div>
                </div>
            </div>
        </div>

        <CreateClientModal
            :show-modal="modalAction === 'showEditModal'" 
            :current-client="currentClient" 
            :current-selection="editModalType"
            :loading="loadingState === 'update_loading'"
            action-type="edit" 
            @cancel="resetEditModal" 
            @complete="updateClientForm"
        />

        <MainModal 
            :show-modal="modalAction === 'showAddLinkModal' || modalAction === 'showEditLinkModal'"
            size="md" 
            :title="modalAction === 'showAddLinkModal' ? 'New link' : 'Edit link'" 
            @close="closeLinkModal"
        >
            <template #body>
                <form @submit.prevent="">
                    <div class="form__row">
                        <FormInput 
                            id="linkName" 
                            v-model="linkForm.name"
                            type="text" 
                            size="sm" 
                            wrapper-class="flex-1"
                            info-type="required"
                            no-margin-bottom
                            html-for="linkName" 
                            label="Link name"
                        />
                        <FormInput 
                            id="linkUrl" 
                            v-model="linkForm.url"
                            type="text" 
                            size="sm" 
                            wrapper-class="flex-1"
                            info-type="required"
                            no-margin-bottom
                            html-for="linkUrl" 
                            label="Link URL"
                        />
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        label="Cancel" 
                        variant="outline" 
                        :disabled="loadingState === 'link_loading'" 
                        @submit="closeLinkModal"
                    />
                    <BaseButton 
                        type="submit" 
                        :loading="loadingState === 'link_loading'" 
                        :disabled="!linkForm.name || !linkForm.url" 
                        label="Save" 
                        @submit="manageLinks"
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import {
    EmptyPage,
    PageSpinner,
    BaseButton,
    CreateClientModal,
    FormLabel,
    MainModal,
} from "@/components/ui/index";
import { industryData } from "@/utils/dummy";
import FormInput from "@/components/ui/input/FormInput.vue";

export default {
    name: "ClientDetailsView",

    components: {
        EmptyPage, 
        BaseButton,
        PageSpinner, 
        MainModal,
        FormLabel,
        FormInput,
        CreateClientModal,
    },

    data() {
        return {
            industries: industryData,
            editModalType: "",
            loading: false,          
            modalAction: "",
            ccEmails: [],
            loadingState: "",
            linkIdToDelete: "",
            linkForm: {
                url: "",
                name: "",
            },
        };
    },

    computed: {
        userProfile() {
            return this.$store.getters["auth/profile"];
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        rateCardsStore() {
            return this.$store.getters["ratecards/rateCards"];
        },

        currentClient() {
            return this.$store.getters["clients/currentClient"] || null;
        },
    },

    methods: {
        openDetailsModal(type) {
            this.editModalType = type;
            this.modalAction = "showEditModal";
        },

        resetEditModal() {  
            this.editModalType = "";
            this.modalAction = "";
        },

        async updateClientForm({ payload, resetFn }) {
            try {
                if (!this.currentClient._id) {
                    throw new Error("Client id is required");
                }

                this.loadingState = "update_loading";
                
                const res = await this.$store.dispatch("clients/updateClient", payload);                
                resetFn();
                this.modalAction = "";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },

        toggleCCEmails() {
            if (this.ccEmails.length) {
                this.ccEmails = [];
            } else {
                this.ccEmails = this.client.ccEmails || [];
            }
        },

        openEditLinkModal(item) {
            this.linkForm = { ...item };
            this.modalAction = "showEditLinkModal";
        },

        closeLinkModal() {
            this.linkForm = {
                name: "",
                url: "",
            };
            this.modalAction = "";
        },

        async manageLinks() {            
            try {                
                if (!this.$route.params.id) {
                    this.$toast.error("Unable to update link");
                    return;
                }

                this.loadingState = "link_loading";

                await this.$store.dispatch("clients/manageLinks", {
                    clientId: this.$route.params.id,
                    payload: this.modalAction === "showAddLinkModal" ? {
                        name: this.linkForm.name,
                        url: this.linkForm.url,
                    } : {
                        name: this.linkForm.name,
                        url: this.linkForm.url,
                        _id: this.linkForm?._id,
                    },
                    params: this.modalAction === "showAddLinkModal" ? { type: "add" } : { type: "update" },
                });
                
                this.closeLinkModal();
            } catch (error) {
                this.loadingState = "link_error";
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },

        async deleteLink(id) {            
            try {                                
                if (!id || !this.$route.params.id) {
                    this.$toast.error("Unable to delete link");
                    return;
                }

                this.linkIdToDelete = id;
                this.loadingState = "delete_link_loading";
                
                await this.$store.dispatch("clients/manageLinks", {
                    clientId: this.$route.params.id,
                    payload: { _id: id },
                    params: { type: "delete" },
                });
            } catch (error) {
                this.loadingState = "delete_link_error";
            } finally {
                this.loadingState = "";
            }
        },

        async addLink() {            
            try {                
                if (!this.$route.params.id) {
                    this.$toast.error("Unable to add link");
                    return;
                }

                this.loadingState = "add_link_loading";
                
                await this.$store.dispatch("clients/manageLinks", {
                    clientId: this.$route.params.id,
                    payload: {
                        name: this.linkForm.name,
                        url: this.linkForm.url,
                    },
                    params: { type: "add" },
                });
                
                this.linkForm = {
                    name: "",
                    link: "",
                };
            } catch (error) {
                this.loadingState = "add_link_error";
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .dropdown-item {
        display: flex;
        align-items: center;
        cursor: pointer;

        &:last-of-type {
            padding-bottom: 0.1rem !important;
        }
    }

    .client-item {
        grid-template-columns: 1fr 1fr;
        gap: 1rem 1rem;
        width: 100%; 
    }
</style>